import { sortBy, uniqBy } from 'lodash-es';
import type { ConversationGroupMessageType, ConversationMessageType } from '@/models/conversations/conversation';
import type { ConversationSessionsTypeUnion, ConversationType } from '@/models/conversations/conversation-session';

export type SessionKey = `session-${number}` | `group-${number}` | string;

export function generateSessionKey(
  type: 'session' | 'group' | 'chat' | 'groupChat' | 1 | 2 | 'friend',
  id?: number | null | undefined,
  defaultKey = 'default',
): SessionKey {
  if (!id) return defaultKey;

  switch (type) {
    case 'session':
    case 'chat':
    case 1:
    case 'friend':
      return `session-${id}` as const;
    case 'group':
    case 'groupChat':
    case 2:
      return `group-${id}` as const;
    default:
      return defaultKey;
  }
}

export function generateSessionKeyFromMessage(
  message: Pick<ConversationMessageType, 'ChatSessionID'> | Pick<ConversationGroupMessageType, 'GroupID'>,
) {
  const isGroupMessage = 'GroupID' in message;
  return generateSessionKey(
    isGroupMessage ? 'group' : 'session',
    isGroupMessage ? message.GroupID : message.ChatSessionID,
  );
}

export function parseSessionKey(key: string): [ConversationType, number] {
  if (!key) return ['session', 0];

  const [type, id] = key.split('-');

  if (type === 'session') return ['session', Number(id)];
  if (type === 'group') return ['group', Number(id)];

  return ['session', 0];
}

export function sortSessions(sessions: ConversationSessionsTypeUnion[]): ConversationSessionsTypeUnion[] {
  return uniqBy(
    sortBy(sessions, (el) => {
      if (el.IsTop) {
        return el.LastMsgTimeStamp * -100;
      }

      return -el.LastMsgTimeStamp;
    }),
    (el) => generateSessionKey(el.SessionType, el.ID),
  );
}
